import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import config from '../config';
import { ReactComponent as ArrowDownIcon } from '../assets/arrow-down.svg';
import { ReactComponent as BackIcon } from '../assets/Back.svg';
import '../scss/components/technicalIssuePage.scss';

const TechnicalIssuePage = () => {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [error, setError] = useState('');

    // Максимальная длина текста в textarea
    const MAX_TEXT_LENGTH = 1000;

    // Состояния для выпадающих списков
    const [isDeviceTypeOpen, setIsDeviceTypeOpen] = useState(false);
    const [isOsTypeOpen, setIsOsTypeOpen] = useState(false);

    // Опции для выпадающих списков
    const deviceTypes = [
        "Смартфон",
        "Планшет",
        "Ноутбук",
        "Компьютер",
        "Браузер"
    ];

    const osTypes = [
        "iOS",
        "Android",
        "Windows",
        "macOS",
        "Linux",
        "Другое"
    ];

    // Состояние формы
    const [formData, setFormData] = useState({
        description: "",
        deviceType: "",
        osType: "",
        stepsToReproduce: "",
        additionalInfo: ""
    });

    // Обработчик изменения полей ввода
    const handleInputChange = (e) => {
        const { name, value } = e.target;

        // Проверка на максимальную длину
        if (value.length <= MAX_TEXT_LENGTH) {
            setFormData(prev => ({ ...prev, [name]: value }));

            // Автоматическое изменение высоты textarea
            if (e.target.tagName === 'TEXTAREA') {
                adjustTextareaHeight(e);
            }
        }
    };

    // Функция для автоматического изменения высоты textarea
    const adjustTextareaHeight = (e) => {
        const textarea = e.target;
        textarea.style.height = 'auto';
        textarea.style.height = `${textarea.scrollHeight}px`;
    };

    // Обработчик отправки формы
    const handleSubmit = async () => {
        // Проверка обязательных полей
        if (!formData.description || !formData.deviceType || !formData.osType) {
            setError('Пожалуйста, заполните обязательные поля: описание проблемы, тип устройства и операционную систему');
            return;
        }

        setIsLoading(true);
        setError('');

        try {
            const initData = window.Telegram.WebApp.initData;
            await axios.post(`${config.API_BASE_URL}/technical-issues/save`, {
                initData,
                issue: formData
            });

            setIsSubmitted(true);
            setTimeout(() => {
                navigate(-1);
            }, 2000);
        } catch (error) {
            console.error('Ошибка при отправке технической проблемы:', error);
            setError('Произошла ошибка при отправке. Пожалуйста, попробуйте позже.');
        } finally {
            setIsLoading(false);
        }
    };

    // Функция для возврата назад
    const goBack = () => {
        navigate(-1);
    };

    // Функция для отображения счетчика символов
    const getCharacterCount = (fieldName) => {
        return `${formData[fieldName].length}/${MAX_TEXT_LENGTH}`;
    };

    return (
        <div className="technical-issue-page">
            <div className="technical-issue-header">
                <button className="back-button" onClick={goBack}>
                    <BackIcon />
                </button>
                <h1>Техническая проблема</h1>
            </div>

            {isSubmitted ? (
                <div className="success-message">
                    <p>Ваше сообщение о технической проблеме успешно отправлено! Мы рассмотрим его в ближайшее время.</p>
                </div>
            ) : (
                <div className="technical-issue-form">
                    {error && (
                        <div className="error-message">
                            <p>{error}</p>
                        </div>
                    )}

                    <div className="form-section">
                        <p className="field-description">1. Детально опишите техническую проблему, с которой вы столкнулись</p>
                        <div className="input-wrapper">
                            <textarea
                                className="textarea-field"
                                name="description"
                                value={formData.description}
                                onChange={handleInputChange}
                                onInput={adjustTextareaHeight}
                                placeholder="Подробно опишите проблему..."
                                maxLength={MAX_TEXT_LENGTH}
                            />
                        </div>
                        <div className="char-counter">{getCharacterCount("description")}</div>
                    </div>

                    <div className="form-section">
                        <p className="field-description">2. На каком устройстве вы используете приложение?</p>
                        <div className="dropdown">
                            <div className="dropdown-header" onClick={() => setIsDeviceTypeOpen(!isDeviceTypeOpen)}>
                                {formData.deviceType || "Выберите тип устройства"}
                                <ArrowDownIcon className={`arrow-icon ${isDeviceTypeOpen ? 'up' : ''}`} />
                            </div>
                            {isDeviceTypeOpen && (
                                <ul className="dropdown-list">
                                    {deviceTypes.map((type) => (
                                        <li key={type} onClick={() => {
                                            setFormData({ ...formData, deviceType: type });
                                            setIsDeviceTypeOpen(false);
                                        }}>
                                            {type}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>

                    <div className="form-section">
                        <p className="field-description">3. Операционная система вашего устройства</p>
                        <div className="dropdown">
                            <div className="dropdown-header" onClick={() => setIsOsTypeOpen(!isOsTypeOpen)}>
                                {formData.osType || "Выберите операционную систему"}
                                <ArrowDownIcon className={`arrow-icon ${isOsTypeOpen ? 'up' : ''}`} />
                            </div>
                            {isOsTypeOpen && (
                                <ul className="dropdown-list">
                                    {osTypes.map((type) => (
                                        <li key={type} onClick={() => {
                                            setFormData({ ...formData, osType: type });
                                            setIsOsTypeOpen(false);
                                        }}>
                                            {type}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                    <div className="form-section">
                        <p className="field-description">4. Есть что-то, что вы хотели бы добавить ещё?</p>
                        <div className="input-wrapper">
                            <textarea
                                className="textarea-field"
                                name="stepsToReproduce"
                                value={formData.stepsToReproduce}
                                onChange={handleInputChange}
                                onInput={adjustTextareaHeight}
                                placeholder="Можете указать какие-то дополнительные данные, которые могут помочь в решении проблемы..."
                                maxLength={MAX_TEXT_LENGTH}
                            />
                        </div>
                        <div className="char-counter">{getCharacterCount("stepsToReproduce")}</div>
                    </div>

                    <div className="form-section">
                        <p className="field-description">5. Можем ли мы связаться с вами, для того, чтобы детально разобраться в проблеме? Если да, напишите например свой логин в Телеграм:</p>
                        <div className="input-wrapper">
                            <textarea
                                className="textarea-field"
                                name="additionalInfo"
                                value={formData.additionalInfo}
                                onChange={handleInputChange}
                                onInput={adjustTextareaHeight}
                                placeholder="Ведите свой логин в Телеграм или иные контакты..."
                                maxLength={MAX_TEXT_LENGTH}
                            />
                        </div>
                        <div className="char-counter">{getCharacterCount("additionalInfo")}</div>
                    </div>

                    <button
                        className={`submit-button ${isLoading ? 'loading' : ''}`}
                        onClick={handleSubmit}
                        disabled={isLoading}
                    >
                        {isLoading ? 'Отправка...' : 'Отправить сообщение'}
                    </button>
                </div>
            )}
        </div>
    );
};

export default TechnicalIssuePage; 